import type { ReactNode } from 'react';
import { Icon, LinkButton, type IconName } from '../../../components/ui';

/**
 * Cartao de canal do hub de disparos.
 *
 * A borda superior colorida e a UNICA cor de canal do cartao: WhatsApp usa
 * `series-3` (o mesmo verde-agua do canal nos graficos), e-mail usa `accent`
 * e a ligacao em lote usa `info`. Titulo, texto e botoes seguem os tons
 * neutros -- cor de serie em texto e proibida pelo guia.
 *
 * `to` ausente = canal ainda nao disponivel: sem botao, com o selo que o
 * chamador passa em `badge`.
 */

export type TomDoCanal = 'whatsapp' | 'email' | 'voz';

const BORDA: Readonly<Record<TomDoCanal, string>> = {
  whatsapp: 'border-t-series-3',
  email: 'border-t-accent',
  voz: 'border-t-info',
};

const ICONE: Readonly<Record<TomDoCanal, string>> = {
  whatsapp: 'bg-series-3/10 text-series-3',
  email: 'bg-accent/10 text-accent',
  voz: 'bg-info/10 text-info',
};

export function CartaoCanal({
  tom,
  icon,
  title,
  description,
  bullets,
  to,
  badge,
  resumo,
  compact = false,
}: {
  readonly tom: TomDoCanal;
  readonly icon: IconName;
  readonly title: string;
  readonly description: string;
  readonly bullets: readonly string[];
  readonly to?: string;
  readonly badge?: ReactNode;
  /** Linha de numeros do periodo, ja formatada (ou esqueleto). */
  readonly resumo?: ReactNode;
  readonly compact?: boolean;
}): JSX.Element {
  return (
    <article className={`flex flex-col gap-4 rounded-2xl border border-line border-t-4 bg-surface shadow-soft ${BORDA[tom]} ${compact ? 'px-5 py-4' : 'px-6 py-6'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span aria-hidden="true" className={`grid shrink-0 place-items-center rounded-xl ${ICONE[tom]} ${compact ? 'h-9 w-9' : 'h-12 w-12'}`}>
            <Icon name={icon} size={compact ? 'sm' : 'lg'} />
          </span>
          <h2 className={`font-semibold tracking-tight text-ink ${compact ? 'text-base' : 'text-xl'}`}>{title}</h2>
        </div>
        {badge}
      </div>
      <p className="text-sm text-ink-muted">{description}</p>
      {bullets.length > 0 ? (
        <ul className="flex flex-col gap-2 text-sm text-ink">
          {bullets.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span aria-hidden="true" className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-status-good/15 text-status-good">
                <Icon name="check" size="xs" />
              </span>
              {item}
            </li>
          ))}
        </ul>
      ) : null}
      {resumo === undefined ? null : <div className="text-xs text-ink-subtle">{resumo}</div>}
      {to === undefined ? null : (
        <div className="mt-auto pt-1">
          <LinkButton to={to} variant="primary" iconRight="seta-dir">
            Abrir
          </LinkButton>
        </div>
      )}
    </article>
  );
}
